import Layout from "../../../components/shared/layout/layout";
import{useRouter} from "next/router";
import {useEffect, useMemo, useRef, useState} from "react";
import Link from "next/link";
import {getCookie, hasCookie, setCookie} from "cookies-next";
import RichText from "../../../components/richText/richText";
import Seo from "../../../components/shared/seo/seo";
import { resolveImageUrl } from "../../../lib/imageUrl";

function getPriceForCountry(countryId, product){
    let price = product.prices.find((price) => price.countryId == countryId).value;
    return price;
}

// --- Package helpers -------------------------------------------------------
// Packages have no own price/space rows — both are derived from the
// contained wines (sum of Austria price * quantity, sum of takesSpace *
// quantity). Mirrors the admin's preview math so cart numbers line up
// with what the customer saw in the catalogue.
function packagePrice(pkg){
    if (!Array.isArray(pkg?.productQuantities)) return 0;
    let total = 0;
    for (const pq of pkg.productQuantities) {
        const p = pq?.product;
        if (!p || !Array.isArray(p.prices)) continue;
        const austria = p.prices.find(pr => pr.countryId === 1);
        const value = (austria ?? p.prices[0])?.value ?? 0;
        total += value * (pq.quantity || 0);
    }
    return total;
}
function packageTakesSpace(pkg){
    if (!Array.isArray(pkg?.productQuantities)) return 0;
    let total = 0;
    for (const pq of pkg.productQuantities) {
        total += (pq?.product?.takesSpace ?? 1) * (pq?.quantity || 0);
    }
    return total;
}

// Flatten a header into a single list of purchasable variants — one row
// per bottle size or linked product, or one row for a package. The `key`
// matches the DOM id used by initProducts so the sticky-bar steppers and
// the main-list qty inputs share a single state.
function collectVariants(product) {
    if (!product) return [];
    if (product.kind === 'Package') {
        return [{
            key: `package-${product.productHeaderId}`,
            id: product.productHeaderId,
            label: `${product.name} ${product.klassifikation ?? ''}`.trim(),
            price: packagePrice(product),
            kind: 'package',
            available: true,
        }];
    }
    const rows = [];
    for (const p of (product.products ?? [])) {
        if (!p?.isVisible) continue;
        rows.push({
            key: `product-${p.productId}`,
            id: p.productId,
            label: `${product.name} ${product.klassifikation ?? ''} ${p.caption ?? ''} ${p.vintage ? p.vintage.split('-')[0] : ''} ${p.capacity != null ? p.capacity + 'l' : ''}`.replace(/\s+/g, ' ').trim(),
            price: getPriceForCountry(1, p),
            kind: 'product',
            available: !!p.isInStock,
        });
    }
    for (const lp of (product.linkedProducts ?? [])) {
        const p = lp?.product;
        if (!p) continue;
        rows.push({
            key: `product-${p.productId}`,
            id: p.productId,
            label: `${p.header?.name ?? ''} ${p.header?.klassifikation ?? ''} ${p.caption ?? ''} ${p.vintage ? p.vintage.split('-')[0] : ''} ${p.capacity != null ? p.capacity + 'l' : ''}`.replace(/\s+/g, ' ').trim(),
            price: getPriceForCountry(1, p),
            kind: 'linked',
            available: !!p.isInStock,
        });
    }
    return rows;
}

function initProducts(product){
    // Packages render as a single quantity row using the computed total —
    // the customer adds N packages to the cart, not N copies of each
    // contained wine. Matches the cart UX requested by the customer.
    if (product?.kind === 'Package') {
        const price = packagePrice(product);
        return [
            <div key={`package-row-${product.productHeaderId}`} className="horizontal mobile"
                 style={{justifyContent: "space-between", alignItems: "center", margin: "1rem 0"}}>
                <input id={`package-${product.productHeaderId}`} name="anzahl"
                       className="search narrow" type="number" min="0" placeholder="0"
                       style={{maxWidth: "50px", margin:"initial"}}/>
                <span style={{margin:"0 1rem"}} className="narrow uppercase size-16">
                    {product.name} {product.klassifikation}
                </span>
                <span style={{margin:"initial", wrap:"no-wrap", minWidth:"50px"}}
                      className="narrow uppercase size-16 right">
                    {`${(Math.round(price * 100) / 100).toFixed(2)} €`}
                </span>
            </div>
        ];
    }

    const rows = [];
    for (var i = 0; i < product.products.length;i++){
        let activeProduct = product.products[i];
        var releaseDate = new Date(activeProduct.releaseDate);
        var dateNow = new Date();
        if(!activeProduct.isVisible) continue;
            rows.push(
                <div className="horizontal mobile" style={{justifyContent: "space-between", alignItems: "center", margin: "1rem 0"}}>
                    {activeProduct.isInStock &&
                    <input id={`product-${activeProduct.productId}`} name="anzahl" className="search narrow"
                           type="number" min="0" placeholder="0" style={{maxWidth: "50px", margin:"initial"}}/>
                    }
                    {!activeProduct.isInStock  && <span className="narrow uppercase size-16" style={{color:"red", margin:"initial"}}>Ausverkauft</span>}
                    {releaseDate > dateNow && <span className="narrow uppercase size-12" style={{color:"red", margin:"initial"}}>Lieferbar ab {releaseDate.toLocaleDateString()}</span>}
                    <span style={{margin:"0 1rem"}}
                        className="narrow uppercase size-16">{product.name} {product.klassifikation} {activeProduct.caption != null && activeProduct.caption} {activeProduct.vintage != null && activeProduct.vintage.split("-")[0]} {activeProduct.capacity}l</span>
                    <span style={{margin:"initial", wrap:"no-wrap", minWidth:"50px"}}
                        className="narrow uppercase size-16 right">{`${(Math.round(getPriceForCountry(1, activeProduct) * 100) / 100).toFixed(2)} €`}</span>
                </div>
            )

    }

    for (var i = 0; i < product.linkedProducts.length;i++){
        let activeProduct = product.linkedProducts[i].product;
        var releaseDate = new Date(activeProduct.releaseDate);
        var dateNow = new Date();
        rows.push(
            <div className="horizontal mobile" style={{justifyContent: "space-between", alignItems: "center", margin: "1rem 0"}}>
                {activeProduct.isInStock &&
                <input id={`product-${activeProduct.productId}`} name="anzahl" className="search narrow"
                       type="number" min="0" placeholder="0" style={{maxWidth: "50px", margin:"initial"}}/>
                }
                {!activeProduct.isInStock  && <span className="narrow uppercase size-16" style={{color:"red", margin:"initial"}}>Ausverkauft</span>}
                {releaseDate > dateNow && <span className="narrow uppercase size-12" style={{color:"red", margin:"initial"}}>Lieferbar ab {releaseDate.toLocaleDateString()}</span>}
                <span style={{margin:"0 1rem"}}
                      className="narrow uppercase size-16">{activeProduct.header.name} {activeProduct.header.klassifikation} {activeProduct.caption != null && activeProduct.caption} {activeProduct.vintage != null && activeProduct.vintage.split("-")[0]} {activeProduct.capacity}l</span>
                <span style={{margin:"initial", wrap:"no-wrap", minWidth:"50px"}}
                      className="narrow uppercase size-16 right">{`${(Math.round(getPriceForCountry(1, activeProduct) * 100) / 100).toFixed(2)} €`}</span>
            </div>
        )

    }

    return rows;
}


export default function Default(){

    const[product, setProduct] = useState(null);
    // Full ordered list of visible product headers — used to compute the
    // prev/next links at the bottom of the page. Fetched once (in parallel
    // with the current product) so the arrows appear the moment the page
    // is ready.
    const[siblings, setSiblings] = useState([]);
    // Sticky CTA is only shown once the in-flow "In den Warenkorb" button
    // has scrolled out of view, so the two never appear together.
    const[showStickyCta, setShowStickyCta] = useState(false);
    // Per-variant qty state — the source of truth for the sticky bar's
    // running total and its expanded stepper rows. Kept in sync with the
    // uncontrolled <input>s in initProducts via a DOM bridge (see below)
    // so we don't have to refactor those.
    const[quantities, setQuantities] = useState({});
    const[stickyExpanded, setStickyExpanded] = useState(false);
    const primaryCtaRef = useRef(null);
    const router = useRouter();
    const { isReady, query: {id,}} = router;

    useEffect(() => {
        const el = primaryCtaRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const io = new IntersectionObserver(
            ([entry]) => setShowStickyCta(!entry.isIntersecting),
            { rootMargin: '0px 0px -20% 0px' }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [product]);

    const variants = useMemo(() => collectVariants(product), [product]);

    // Wipe quantities when a new product loads so the summary starts at 0.
    useEffect(() => {
        setQuantities({});
        setStickyExpanded(false);
    }, [product?.productHeaderId]);

    // DOM bridge: mirror every uncontrolled qty <input> into `quantities`
    // whenever the user types. Runs after initProducts has rendered its
    // rows so `document.getElementById` finds them.
    useEffect(() => {
        if (!product) return undefined;
        const cleanups = [];
        for (const v of variants) {
            const el = document.getElementById(v.key);
            if (!el) continue;
            const handler = () => {
                const n = Math.max(0, parseInt(el.value) || 0);
                setQuantities((q) => (q[v.key] === n ? q : { ...q, [v.key]: n }));
            };
            handler();
            el.addEventListener('input', handler);
            cleanups.push(() => el.removeEventListener('input', handler));
        }
        return () => cleanups.forEach((fn) => fn());
    }, [product, variants]);

    // Setter used by the sticky-bar steppers. `next` is either an absolute
    // number or a (curr) => newVal function so back-to-back +/- clicks
    // don't stack on a stale closure. DOM sync happens in a separate
    // effect below.
    function setVariantQty(key, next) {
        setQuantities((q) => {
            const curr = q[key] || 0;
            const raw = typeof next === 'function' ? next(curr) : Number(next);
            const n = Math.max(0, Math.floor(raw) || 0);
            return { ...q, [key]: n };
        });
    }

    // Mirror state → DOM. Writing to input.value doesn't fire an 'input'
    // event, so the bridge effect above stays a one-way "DOM → state"
    // sync and there's no feedback loop.
    useEffect(() => {
        for (const v of variants) {
            const el = document.getElementById(v.key);
            if (!el) continue;
            const desired = quantities[v.key] ? String(quantities[v.key]) : '';
            if (el.value !== desired) el.value = desired;
        }
    }, [quantities, variants]);

    const totalCount = variants.reduce((s, v) => s + (quantities[v.key] || 0), 0);
    const totalPrice = variants.reduce((s, v) => s + (quantities[v.key] || 0) * v.price, 0);
    const hasMultipleVariants = variants.length > 1;

    useEffect(() => {
        if (!isReady) {

            return undefined;
        }
        // Reset while the new product loads so stale content doesn't flash
        // between clicks on the sticky prev/next pills.
        setProduct(null);
        fetch(`https://api.gisperg.wine/api/Product/Headers/${id}`, {
            method: 'GET',
            mode: 'cors'
        })
            .then((res) => res.json())
            .then((data) => {
                setProduct(data)
            })
    }, [isReady, id]);

    // Load the sibling catalogue once. Filter to visible entries so the
    // prev/next arrows never skip into a hidden product, and sort by the
    // admin-defined display order so the browsing sequence matches the
    // shop grid.
    useEffect(() => {
        fetch(`https://api.gisperg.wine/api/Product/Headers`, {
            method: 'GET',
            mode: 'cors',
        })
            .then((res) => res.json())
            .then((data) => {
                const visible = (Array.isArray(data) ? data : [])
                    .filter((p) => p?.isVisible !== false)
                    .sort((a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0));
                setSiblings(visible);
            })
            .catch(() => setSiblings([]));
    }, []);

    // Resolve previous / next relative to the current product. Wraps around
    // the ends of the list so the arrows never dead-end.
    const currentIndex = siblings.findIndex((p) => String(p.productHeaderId) === String(id));
    const prevProduct = siblings.length > 0
        ? siblings[(currentIndex - 1 + siblings.length) % siblings.length]
        : null;
    const nextProduct = siblings.length > 0
        ? siblings[(currentIndex + 1) % siblings.length]
        : null;

    function addProductToCart(product) {
        // Package: read the single combined-qty input, store ONE cookie
        // identified by the package's header id. The cookie carries the
        // contents map so the order POST can later expand the package
        // into individual CartItems for fulfilment.
        if (product?.kind === 'Package') {
            const inputElement = document.getElementById(`package-${product.productHeaderId}`);
            const count = inputElement ? parseInt(inputElement.value) : 0;
            if (!count || count <= 0) return;
            const cookieKey = `cart-package-${product.productHeaderId}`;
            let cookieEntry;
            if (hasCookie(cookieKey)) {
                cookieEntry = JSON.parse(decodeURIComponent(getCookie(cookieKey)));
                cookieEntry.count += count;
            } else {
                cookieEntry = {
                    kind: 'package',
                    id: product.productHeaderId,
                    name: `${product.name} ${product.klassifikation ?? ''}`.trim(),
                    count: count,
                    price: packagePrice(product),
                    takesSpace: packageTakesSpace(product),
                    taxRate: product.taxRate,
                    // Embed the contents so kassa can expand to CartItems
                    // for the order POST without re-fetching the package.
                    contents: (product.productQuantities ?? []).map(pq => ({
                        productId: pq.productId,
                        quantity: pq.quantity,
                    })),
                };
            }
            setCookie(cookieKey, JSON.stringify(cookieEntry));
            router.push("/weinkauf/shop");
            return;
        }
        for (var i = 0; i < product.products.length;i++) {
            let activeProduct = product.products[i];
            let inputElement = document.getElementById(`product-${activeProduct.productId}`);
            let cookieProduct;
            if(inputElement != null && inputElement.value > 0){
                if(hasCookie(`cart-product-${activeProduct.productId}`)){
                    let cookie = getCookie(`cart-product-${activeProduct.productId}`);
                    cookieProduct = JSON.parse(decodeURIComponent(cookie));
                    cookieProduct.count += parseInt(inputElement.value);
                }else {
                    cookieProduct = {
                        id: activeProduct.productId,
                        name: `${product.name} ${product.klassifikation} ${activeProduct.caption != null && activeProduct.caption} ${activeProduct.vintage != null ? activeProduct.vintage.split("-")[0] : ""}`,
                        capacity: activeProduct.capacity,
                        count: parseInt(inputElement.value),
                        takesSpace : activeProduct.takesSpace,
                        price: getPriceForCountry(1, activeProduct),
                        taxRate: product.taxRate
                    };
                }
                setCookie(`cart-product-${activeProduct.productId}`, JSON.stringify(cookieProduct));
                router.push("/weinkauf/shop");
            }
        }
        for (var i = 0; i < product.linkedProducts.length;i++) {
            let activeProduct = product.linkedProducts[i].product;
            let inputElement = document.getElementById(`product-${activeProduct.productId}`);
            let cookieProduct;
            if(inputElement.value > 0){
                if(hasCookie(`cart-product-${activeProduct.productId}`)){
                    let cookie = getCookie(`cart-product-${activeProduct.productId}`);
                    cookieProduct = JSON.parse(decodeURIComponent(cookie));
                    cookieProduct.count += parseInt(inputElement.value);
                }else {
                    cookieProduct = {
                        id: activeProduct.productId,
                        name: `${activeProduct.header.name} ${activeProduct.header.klassifikation} ${activeProduct.caption != null && activeProduct.caption} ${activeProduct.vintage != null ? activeProduct.vintage.split("-")[0] : ""}`,
                        capacity: activeProduct.capacity,
                        count: parseInt(inputElement.value),
                        takesSpace : activeProduct.takesSpace,
                        price: getPriceForCountry(1, activeProduct),
                        taxRate: product.taxRate
                    };
                }
                setCookie(`cart-product-${activeProduct.productId}`, JSON.stringify(cookieProduct));
                router.push("/weinkauf/shop");
            }
        }
    }

    return(
        <Layout>
            <Seo
                title={product ? `${product.name} ${product.klassifikation}` : "Wein"}
                description={
                    product
                        ? `${product.name} ${product.klassifikation} ${product.declaration ?? ''} — Weingut Gisperg`
                        : "Wein aus dem Weingut Gisperg"
                }
                path={`/weinkauf/shop/details${router?.query?.id ? `?id=${router.query.id}` : ''}`}
                type="product"
                image={resolveImageUrl(product?.showCaseImageLocation, 'shop', '.webp') ?? undefined}
            />
            <section className="background_light" style={{maxWidth:"auto", width:"100%"}}>
                <Link scroll={false} href="/weinkauf/shop">
                    <p className="annotation marker-back black goBack"><b>Zurück zum Shop</b></p>
                </Link>
                {product != null && <div className="horizontal" style={{maxWidth:"auto", width:"100%", alignItems:"flex-start"}}>
                <div className="col-4" style={{alignSelf:"flex-start"}}>
                    {/* Match the tile aspect on the index page so a portrait
                        bottle and a square phone photo both render in a
                        predictable slot here. */}
                    <img alt="" src={resolveImageUrl(product.showCaseImageLocation, 'shop', '.webp')} style={{width:"100%", aspectRatio:"2 / 3", objectFit:"contain", display:"block"}}/>
                </div>
                <div className="col-8" style={{zIndex:2}}>
                    <h1 className="didot-bold uppercase size-40" style={{marginBottom:0}}>{product.name}</h1>
                    <h1 className="didot-bold uppercase size-40" style={{marginTop:0, marginBottom:0}}>{product.klassifikation}</h1>
                    {/* Subheading is normally derived from tax rate (13% = Qualitätswein, 10% = naturreiner Traubensaft, otherwise Schaumwein).
                        For a package we instead aggregate the categories of the contained wines so a mixed package shows e.g. "Qualitätswein · Schaumwein". */}
                    {(() => {
                        const taxRateToLabel = (rate) => {
                            if (rate === 13) return "Qualitätswein";
                            if (rate === 10) return "Naturreiner Traubensaft";
                            if (rate === 20) return "Schaumwein";
                            return null;
                        };
                        if (product.kind === 'Package' && Array.isArray(product.productQuantities)) {
                            const labels = new Set();
                            for (const pq of product.productQuantities) {
                                const rate = pq?.product?.header?.taxRate;
                                const label = taxRateToLabel(rate);
                                if (label) labels.add(label);
                            }
                            if (labels.size === 0) return null;
                            return <h2 className="narrow uppercase size-16" style={{marginTop:0}}>{Array.from(labels).join(" · ")}</h2>;
                        }
                        const label = taxRateToLabel(product.taxRate);
                        return label ? <h2 className="narrow uppercase size-16" style={{marginTop:0}}>{label}</h2> : null;
                    })()}
                    {product.declaration !== "" && <h2 className="narrow uppercase size-16" style={{marginTop:0}}>{product.declaration}</h2>}
                    <RichText html={product.description} className="narrow size-16"/>
                    {product.kind === 'Package' && Array.isArray(product.productQuantities) && product.productQuantities.length > 0 && (
                        // .col-8 has `align-items: center` so flex children
                        // shrink to their content. alignSelf:stretch + width:100%
                        // force this block to fill the column so the contents
                        // sit flush-left like the description above.
                        <div style={{margin: "1.5rem 0", textAlign: "left", alignSelf: "stretch", width: "100%"}}>
                            {/* Mirror the top-of-page product headlines so the
                                contents section feels like a sub-header:
                                fixed line + the package's klassifikation. */}
                            <h1 className="didot-bold uppercase size-40" style={{marginBottom: 0, textAlign: "left"}}>
                                Dieses Paket enthält
                            </h1>
                            {product.klassifikation && (
                                <h1 className="didot-bold uppercase size-40" style={{marginTop: 0, marginBottom: "1rem", textAlign: "left"}}>
                                    {product.klassifikation}
                                </h1>
                            )}
                            <div style={{display: "flex", flexDirection: "column", gap: "0.75rem", alignItems: "stretch", width: "100%"}}>
                                {/* Stable copy + sort so the admin's drag-to-reorder
                                    in the package editor controls the public order. */}
                                {[...product.productQuantities]
                                    .sort((a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0))
                                    .map((pq) => {
                                    const p = pq.product;
                                    if (!p) return null;
                                    const headerName = p.header ? `${p.header.name} ${p.header.klassifikation ?? ''}`.trim() : '';
                                    const vintage = p.vintage ? p.vintage.split('-')[0] : '';
                                    const caption = p.caption ?? '';
                                    const capacity = p.capacity != null ? `${p.capacity}l` : '';
                                    const line = `${pq.quantity} × ${headerName} ${caption} ${vintage} ${capacity}`
                                        .replace(/\s+/g, ' ').trim();
                                    const hasNote = pq.note && pq.note.trim().length > 0;
                                    return (
                                        <div
                                            key={pq.productQuantityId ?? p.productId}
                                            className="narrow size-16"
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: hasNote ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
                                                gap: "0.75rem",
                                                alignItems: "start",
                                                borderTop: "1px dotted #c8b9a3",
                                                paddingTop: "0.5rem",
                                            }}
                                        >
                                            <div>{line}</div>
                                            {hasNote && (
                                                <div style={{fontStyle: "italic", color: "#7C6765"}}>
                                                    {pq.note}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {initProducts(product)}
                    <p ref={primaryCtaRef} className="button primary x3" onClick={() => addProductToCart(product)}>In den Warenkorb</p>
                </div>
            </div>}

            {/* Sticky prev / next pills, anchored to the vertical middle
                of the viewport so they're reachable without scrolling to
                the bottom of the page. Names truncate to one line — on
                narrow screens the pill collapses to just the arrow so a
                long wine name can never wrap or push the pill off-edge. */}
            {product != null && siblings.length > 1 && (
                <>
                    {prevProduct && (
                        <Link href={`/weinkauf/shop/details?id=${prevProduct.productHeaderId}`} scroll={true}>
                            <p className="annotation marker-back black goBack siblingNav siblingNav-prev">
                                <b className="siblingNav-label">{prevProduct.name}</b>
                            </p>
                        </Link>
                    )}
                    {nextProduct && (
                        <Link href={`/weinkauf/shop/details?id=${nextProduct.productHeaderId}`} scroll={true}>
                            <p className="annotation marker black siblingNav siblingNav-next">
                                <b className="siblingNav-label">{nextProduct.name}</b>
                            </p>
                        </Link>
                    )}
                </>
            )}
            {/* Sticky bottom action bar. Collapses to a summary + CTA;
                expands to per-variant steppers when the header has more
                than one bottle size / linked product. The steppers write
                into the same DOM inputs initProducts rendered, so the
                existing addProductToCart(product) picks them up
                unchanged. Hidden while the in-flow CTA is on screen. */}
            {product != null && showStickyCta && (
                <div className={`stickyCta ${showStickyCta ? 'stickyCta-visible' : ''}`}>
                    {stickyExpanded && hasMultipleVariants && (
                        <div className="stickyCta-variants">
                            {variants.map((v) => {
                                const n = quantities[v.key] || 0;
                                return (
                                    <div key={v.key} className="stickyCta-variantRow">
                                        <span className="narrow uppercase size-14 stickyCta-variantLabel">{v.label}</span>
                                        {v.available ? (
                                            <div className="stickyCta-stepper" role="group" aria-label={`Menge ${v.label}`}>
                                                <button type="button" onClick={() => setVariantQty(v.key, (c) => c - 1)} aria-label="weniger">−</button>
                                                <span aria-live="polite">{n}</span>
                                                <button type="button" onClick={() => setVariantQty(v.key, (c) => c + 1)} aria-label="mehr">+</button>
                                            </div>
                                        ) : (
                                            <span className="narrow size-14" style={{color:"red"}}>Ausverkauft</span>
                                        )}
                                        <span className="narrow uppercase size-14 stickyCta-variantPrice">
                                            {`${(Math.round(v.price * 100) / 100).toFixed(2)} €`}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div
                        className={`stickyCta-inner ${hasMultipleVariants ? 'stickyCta-clickable' : ''}`}
                        onClick={hasMultipleVariants ? () => setStickyExpanded((e) => !e) : undefined}
                    >
                        <div className="stickyCta-name">
                            {totalCount > 0 ? (
                                <>
                                    <span className="narrow uppercase size-16">
                                        {totalCount} {totalCount === 1 ? 'Flasche' : 'Flaschen'}
                                    </span>
                                    <span className="narrow uppercase size-14 stickyCta-sub">
                                        {`${(Math.round(totalPrice * 100) / 100).toFixed(2)} €`}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="narrow uppercase size-16">{product.name}</span>
                                    {hasMultipleVariants ? (
                                        <span className="narrow uppercase size-14 stickyCta-sub">
                                            {variants.length} Größen — antippen zum Auswählen
                                        </span>
                                    ) : product.klassifikation && (
                                        <span className="narrow uppercase size-14 stickyCta-sub">{product.klassifikation}</span>
                                    )}
                                </>
                            )}
                        </div>
                        {/* Single-variant products get an inline stepper right in
                            the collapsed bar — otherwise there's no way to set a
                            qty without scrolling back to the main list. */}
                        {!hasMultipleVariants && variants[0]?.available && (() => {
                            const v = variants[0];
                            const n = quantities[v.key] || 0;
                            return (
                                <div className="stickyCta-stepper" role="group" aria-label="Menge" onClick={(e) => e.stopPropagation()}>
                                    <button type="button" onClick={() => setVariantQty(v.key, (c) => c - 1)} aria-label="weniger">−</button>
                                    <span aria-live="polite">{n}</span>
                                    <button type="button" onClick={() => setVariantQty(v.key, (c) => c + 1)} aria-label="mehr">+</button>
                                </div>
                            );
                        })()}
                        {hasMultipleVariants && (
                            <button
                                type="button"
                                className="stickyCta-toggle"
                                aria-expanded={stickyExpanded}
                                aria-label={stickyExpanded ? "Zuklappen" : "Aufklappen"}
                                onClick={(e) => { e.stopPropagation(); setStickyExpanded((x) => !x); }}
                            >{stickyExpanded ? '▾' : '▴'}</button>
                        )}
                        <p
                            className={`button primary x3 stickyCta-btn ${totalCount === 0 ? 'stickyCta-btn-disabled' : ''}`}
                            onClick={(e) => { e.stopPropagation(); if (totalCount > 0) addProductToCart(product); }}
                        >In den Warenkorb</p>
                    </div>
                </div>
            )}
            </section>
            <style jsx>{`
                :global(.stickyCta) {
                    position: fixed;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 1001;
                    background: rgba(247, 247, 247, 0.96);
                    backdrop-filter: saturate(140%) blur(6px);
                    box-shadow: rgba(100, 100, 111, 0.18) 0 -4px 18px 0;
                    padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
                    transform: translateY(100%);
                    transition: transform 220ms ease-out;
                }
                :global(.stickyCta-visible) { transform: translateY(0); }
                :global(.stickyCta-inner) {
                    max-width: 1140px;
                    margin: 0 auto;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                }
                :global(.stickyCta-name) {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                    line-height: 1.2;
                }
                :global(.stickyCta-name > span) {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                :global(.stickyCta-sub) { color: #7C6765; }
                :global(.stickyCta-btn) { margin: 0 !important; white-space: nowrap; }
                :global(.stickyCta-btn-disabled) {
                    opacity: 0.45;
                    pointer-events: none;
                    cursor: default;
                }
                :global(.stickyCta-clickable) { cursor: pointer; }
                :global(.stickyCta-toggle) {
                    background: transparent;
                    border: 1px solid #c8b9a3;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    line-height: 1;
                    color: #7C6765;
                    cursor: pointer;
                    flex: none;
                }
                :global(.stickyCta-variants) {
                    max-width: 1140px;
                    margin: 0 auto;
                    padding: 0.5rem 0 0.75rem;
                    border-bottom: 1px solid #e2d8c9;
                    max-height: 40vh;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                :global(.stickyCta-variantRow) {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) auto auto;
                    align-items: center;
                    gap: 0.75rem;
                }
                :global(.stickyCta-variantLabel) {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                :global(.stickyCta-variantPrice) {
                    min-width: 60px;
                    text-align: right;
                }
                :global(.stickyCta-stepper) {
                    display: inline-flex;
                    align-items: center;
                    border: 1px solid #c8b9a3;
                    border-radius: 999px;
                    overflow: hidden;
                }
                :global(.stickyCta-stepper button) {
                    background: transparent;
                    border: 0;
                    width: 32px;
                    height: 32px;
                    font-size: 18px;
                    line-height: 1;
                    color: #7C6765;
                    cursor: pointer;
                }
                :global(.stickyCta-stepper button:hover) { background: rgba(0,0,0,0.05); }
                :global(.stickyCta-stepper span) {
                    display: inline-block;
                    min-width: 28px;
                    text-align: center;
                    font-family: GothamNarrowBook, Gotham, sans-serif;
                    font-size: 14px;
                    color: #333;
                }
                @media only screen and (max-width: 43rem) {
                    :global(.stickyCta) { padding: 0.6rem 0.75rem calc(0.6rem + env(safe-area-inset-bottom)); }
                    :global(.stickyCta-inner) { gap: 0.5rem; }
                    :global(.stickyCta-sub) { font-size: 11px !important; }
                    /* Single-variant inline stepper already shows the qty,
                       so drop the name column to free up room for the pill
                       button that would otherwise overlap. */
                    :global(.stickyCta-inner:has(> .stickyCta-stepper) .stickyCta-name) {
                        display: none;
                    }
                    :global(.stickyCta-variantRow) {
                        grid-template-columns: minmax(0, 1fr) auto;
                        grid-template-areas:
                            "label price"
                            "stepper stepper";
                        row-gap: 0.35rem;
                    }
                    :global(.stickyCta-variantLabel) { grid-area: label; }
                    :global(.stickyCta-variantPrice) { grid-area: price; }
                    :global(.stickyCta-stepper) { grid-area: stepper; justify-self: end; }
                }
                :global(.siblingNav) {
                    position: fixed;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 1000;
                    margin: 0;
                    padding: 0.75rem 1rem;
                    background: rgba(247, 247, 247, 0.92);
                    box-shadow: rgba(100, 100, 111, 0.2) 0 4px 12px 0;
                    cursor: pointer;
                    max-width: 22vw;
                    display: flex;
                    align-items: center;
                }
                :global(.siblingNav-prev) { left: 1rem; }
                :global(.siblingNav-next) { right: 1rem; }
                :global(.siblingNav-label) {
                    display: inline-block;
                    max-width: 16ch;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                /* On narrow screens the arrow-pseudo alone stays visible;
                   the name is hidden so the pill can't grow into the page
                   content or wrap to two lines. */
                @media only screen and (max-width: 43rem) {
                    :global(.siblingNav) {
                        padding: 0.6rem 0.75rem;
                        max-width: none;
                    }
                    :global(.siblingNav-prev) { left: 0.5rem; }
                    :global(.siblingNav-next) { right: 0.5rem; }
                    :global(.siblingNav-label) { display: none; }
                }
            `}</style>
        </Layout>
    )
}


async function getProductById(id){
    const response = await fetch(`https://api.gisperg.wine/api/Product/Headers/${id}`,{
        method:'GET',
        mode: 'cors'
    });
    const json =  await response.json();
    return json;
}